<template>
  <teleport to="body">
    <div class="hunger-overlay" @click.self="$emit('close')">
      <div class="hunger-panel" @touchmove.stop @wheel.stop>

        <!-- Заголовок -->
        <div class="hunger-header">
          {{ title || (type === 'hunger' ? 'Чувство голода до еды' : 'Чувство сытости после еды') }}
        </div>

        <!-- Барабан -->
        <div class="wheel-wrapper">
          <!-- Fade сверху -->
          <div class="wheel-fade-top"></div>
          <!-- Fade снизу -->
          <div class="wheel-fade-bottom"></div>
          <!-- Рамка выбора -->
          <div class="highlight-bar"></div>

          <div class="wheel-scroll" ref="scrollEl" @scroll="onScroll">
            <div class="wheel-pad"></div>
            <div
              v-for="item in items"
              :key="item.value"
              class="wheel-row"
            >
              <div class="wheel-circle" :style="{ background: SMILEY_COLORS[item.value] }">
                {{ item.value }}
              </div>
              <div class="wheel-text">
                <div class="wheel-title">{{ item.title }}</div>
                <div class="wheel-desc">{{ item.desc }}</div>
              </div>
            </div>
            <div class="wheel-pad"></div>
          </div>
        </div>

        <!-- Кнопки -->
        <div class="hunger-actions">
          <button class="hunger-save" @click="save">Сохранить</button>
          <button class="hunger-reset" @click="reset">Сбросить</button>
        </div>

      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { SMILEY_COLORS } from '../utils/smileyUtils.js'

const props = defineProps({
  type: { type: String, required: true },
  modelValue: { type: Number, default: null },
  title: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'close'])

// Значение по умолчанию — не середина шкалы, а типичная точка для этого вопроса:
// 3 — здоровый голод (хорошее время начать есть), 7 — комфортная сытость (хорошее время закончить).
const DEFAULT_VALUE = props.type === 'hunger' ? 3 : 7

const scrollEl = ref(null)
const selected = ref(props.modelValue ?? DEFAULT_VALUE)

const items = [
  { value: 10, title: 'Болезненное переедание', desc: 'Живот распирает, возможна тошнота. «Смотреть на еду не могу».' },
  { value: 9,  title: 'Сильное переедание',     desc: 'Явный дискомфорт, хочется ослабить ремень, прилечь, сонливость.' },
  { value: 8,  title: 'Переедание',             desc: 'Лёгкая тяжесть и стянутость в животе. Ещё не больно, но уже не приятно.' },
  { value: 7,  title: 'Комфортная сытость',     desc: 'Приятная наполненность, удовлетворение. Желания есть больше нет. Хороший момент закончить.' },
  { value: 6,  title: 'Лёгкая сытость',         desc: 'Голод ушёл, наелся — хотя место ещё есть. Здесь уже можно остановиться.' },
  { value: 5,  title: 'Нейтрально',             desc: 'Ни голода, ни сытости. Тело ничего не просит.' },
  { value: 4,  title: 'Лёгкий голод',           desc: 'Первые сигналы: мысли о еде появляются чаще, но можно и подождать.' },
  { value: 3,  title: 'Голод',                  desc: 'Желудок пуст, готов есть — но без спешки. Лучший момент начать.' },
  { value: 2,  title: 'Сильный голод',          desc: 'Пустота и сосание в желудке, всё вокруг кажется вкусным, трудно сосредоточиться.' },
  { value: 1,  title: 'Зверский голод',         desc: 'Раздражительность, головокружение, тревожность, «готов съесть что угодно».' },
]

// Строка выросла вместе с кеглем описания: 16px в четыре строки + заголовок
// в 96px уже не помещались.
const ROW_H = 112

function onScroll() {
  if (!scrollEl.value) return
  const idx = Math.round(scrollEl.value.scrollTop / ROW_H)
  const clamped = Math.max(0, Math.min(idx, items.length - 1))
  selected.value = items[clamped].value
}

function save() {
  emit('update:modelValue', selected.value)
  emit('close')
}

function reset() {
  emit('update:modelValue', null)
  emit('close')
}

// Блокируем скролл body при открытии попапа
let savedOverflow = ''
onMounted(() => {
  savedOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  nextTick(() => {
    if (!scrollEl.value) return
    const initial = props.modelValue ?? DEFAULT_VALUE
    const idx = items.findIndex(i => i.value === initial)
    if (idx >= 0) {
      scrollEl.value.scrollTop = idx * ROW_H
    }
  })
})

onBeforeUnmount(() => {
  document.body.style.overflow = savedOverflow
})
</script>

<style scoped>
.hunger-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.hunger-panel {
  width: 100%;
  max-width: 430px;
  height: 88vh;
  background: #FAF3E8;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
  user-select: none;
  -webkit-user-select: none;
}

.hunger-header {
  font-family: Inter, sans-serif;
  font-size: var(--fs-20);
  font-weight: 700;
  color: var(--ink);
  text-align: center;
  padding: 24px 24px 12px;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
}

.wheel-wrapper {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Fade сверху — уходит под заголовок */
.wheel-fade-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(to bottom, #FAF3E8 0%, rgba(250,243,232,0) 100%);
  pointer-events: none;
  z-index: 2;
}

/* Fade снизу — уходит к кнопкам */
.wheel-fade-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(to top, #FAF3E8 0%, rgba(250,243,232,0) 100%);
  pointer-events: none;
  z-index: 2;
}

.highlight-bar {
  position: absolute;
  top: 50%;
  left: 10px;
  right: 10px;
  height: 112px;
  transform: translateY(-50%);
  border: 1.5px solid rgba(65, 41, 26, 0.8);
  border-radius: 16px;
  pointer-events: none;
  z-index: 1;
}

.wheel-scroll {
  height: 100%;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.wheel-scroll::-webkit-scrollbar {
  display: none;
}

.wheel-pad {
  height: calc(50% - 56px);
  flex-shrink: 0;
}

.wheel-row {
  height: 112px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 24px;
  scroll-snap-align: center;
}

/* Кружок уровня: цвет шкалы + цифра внутри (смайликов здесь больше нет) */
.wheel-circle {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: var(--fs-28);
  font-weight: 700;
  line-height: 1;
  color: var(--ink);
}

.wheel-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.wheel-title {
  font-family: Inter, sans-serif;
  font-size: var(--fs-16);
  font-weight: 700;
  color: var(--ink);
  line-height: 1.25;
}

.wheel-desc {
  font-family: Inter, sans-serif;
  font-size: var(--fs-16);
  font-weight: 400;
  color: rgba(65, 41, 26, 0.8);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hunger-actions {
  flex-shrink: 0;
  /* 32px под нижней кнопкой — как pb-8 у остальных bottom-sheet'ов */
  padding: 12px 24px max(32px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 3;
}

.hunger-save {
  width: 100%;
  height: 52px;
  border-radius: 14px;
  background: #41291A;
  color: var(--cream);
  font-family: Inter, sans-serif;
  font-size: var(--fs-17);
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.hunger-reset {
  width: 100%;
  height: 52px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #E0E0E0;
  font-family: Inter, sans-serif;
  font-size: var(--fs-17);
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
}
</style>
